import type { RootOrData } from 'plotly.js-dist-min';
import { toImage } from 'plotly.js-dist-min';

import { base64PlotPlaceholder } from '../state/dashboard/base64images';
import {
  normalizeTextWidgetExportContent,
  readPlainTextFromTextWidgetDom
} from './plainTextExport';

export {
  normalizeTextWidgetExportContent,
  plainTextFromLexicalJson,
  readPlainTextFromTextWidgetDom
} from './plainTextExport';

// -- type definitions --

interface ExportPlotlyWidgetToPngOptions {
  width: number;
  height: number;
}

// -- constants --

const defaultExportPlotlyWidgetToPngOptions: ExportPlotlyWidgetToPngOptions = {
  width: 500,
  height: 500,
};

// -- main functions --

/**
 * Export any Plotly-based widget content to PNG.
 * 
 * The image is 500x500px by default.
 */
export async function exportPlotlyWidgetToPng(
  widgetId: string,
  options: ExportPlotlyWidgetToPngOptions = defaultExportPlotlyWidgetToPngOptions
): Promise<string> {
  const target = document.querySelector(`#widget-${widgetId}`);
  if (!target) {
    return base64PlotPlaceholder;
  }

  const graphDiv = target.querySelector('.js-plotly-plot');
  if (!graphDiv) {
    return base64PlotPlaceholder;
  }

  return toImage(graphDiv as RootOrData, {
    format: 'png',
    width: options.width,
    height: options.height,
    scale: 1,
  });
}

/** Export table HTML from a widget container. */
export function exportTableWidgetToHtml(widgetId: string): string {
  const target = document.querySelector(`#widget-${widgetId}`);
  if (!target) {
    return '<table><tr><td>&lt;tableau non fourni ou non trouvé&gt;</td></tr></table>';
  }

  const table = target.querySelector('.table');
  if (!table) {
    return '<table><tr><td>&lt;tableau non fourni ou non trouvé&gt;</td></tr></table>';
  }

  const clonedTable = table.cloneNode(true) as Element;
  clonedTable
    .querySelectorAll('th button, td button')
    .forEach((button) => button.remove());

  return clonedTable.outerHTML;
}

/**
 * Export Leaflet-based widget content to PNG.
 * 
 * The image is the size of the widget container.
 */
export async function exportMapWidgetToPng(widgetId: string): Promise<string | null> {
  const target = document.querySelector(`#widget-${widgetId}`);
  if (!target) {
    return null;
  }

  const map = target.querySelector('.leaflet-container');
  if (!map) {
    return null;
  }

  const { toPng } = await import('html-to-image');
  const width = map.getBoundingClientRect().width;
  const height = map.getBoundingClientRect().height;
  return toPng(map as HTMLElement, {
    width,
    height,
    skipFonts: true,
  });
}

/** Export image widget to PNG data URL. */
export async function exportImageWidgetToPng(
  widgetId: string,
  elementIndex = 0,
  recursionLevel = 0
): Promise<string | undefined> {
  const target = document.querySelector(`#widget-${widgetId}`);
  if (!target) {
    return undefined;
  }

  const img =
    recursionLevel === 0
      ? target.querySelector('img')
      : target.querySelectorAll('img')[elementIndex];
  if (!img) {
    return undefined;
  }

  if (img.src.startsWith('data:image/png;base64,')) {
    return img.src;
  }

  const { toPng } = await import('html-to-image');
  return toPng(img as HTMLElement, { skipFonts: true });
}

/**
 * Export text widget content as plain text or localized date string.
 *
 * Prefers conversion from value directly if provided,
 * otherwise finds it in the DOM via the widget ID.
 */
export function exportTextWidgetContent(
  widgetId: string,
  textValue: string | undefined = undefined,
  locale: string | undefined = undefined,
): string {
  if (textValue !== undefined) {
    return normalizeTextWidgetExportContent(textValue, locale);
  }

  return readPlainTextFromTextWidgetDom(widgetId, locale);
}
